<template>
  <div class="container py-5">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else>
      <div v-if="!testComponent" class="alert alert-warning">
        Type de test non reconnu : {{ testType }}
      </div>
      
      <component 
        v-else
        :is="testComponent"
        :test="testData"
        :assigned-test-id="assignedTestId"
        :initial-progress="progress"
        :time-limit="timeLimit"
        @completed="onTestCompleted"
      />
    </div>
  </div>
</template>

<script>
import TestPersonality from '@/components/tests/TestPersonality.vue'
import TestManagerialPotential from '@/components/tests/TestManagerialPotential.vue'
import TestCommercialPotential from '@/components/tests/TestCommercialPotential.vue'
import TestBehavioralStyle from '@/components/tests/TestBehavioralStyle.vue'
import MotivationTest from '@/components/tests/MotivationTest.vue'

export default {
  name: 'TestTaking',

  components: {
    TestPersonality,
    TestManagerialPotential,
    TestCommercialPotential,
    TestBehavioralStyle,
    MotivationTest
  },

  inject: ['ToastSuccess', 'ToastError'],

  data() {
    return {
      loading: true,
      error: null,
      testData: null,
      testType: null,
      assignedTestId: null,
      progress: null,
      timeLimit: 0
    }
  },

  computed: {
    testComponent() {
      const components = {
        personality: TestPersonality,
        managerial: TestManagerialPotential,
        commercial: TestCommercialPotential,
        behavioral: TestBehavioralStyle,
        motivation: MotivationTest
      }
      return components[this.testType] || null
    }
  },

  async created() {
    // Récupérer l'ID du test depuis les paramètres de route
    const testId = this.$route.params.id || this.$route.query.testId
    if (!testId) {
      this.error = 'ID du test non spécifié'
      this.loading = false
      return
    }

    try {
      // Démarrer le test
      const startResponse = await this.$http.post(`/tests/${testId}/start`)
      if (startResponse.data.assignedTestId) {
        this.assignedTestId = startResponse.data.assignedTestId
        await this.loadTestData()
      } else {
        throw new Error('ID du test assigné non trouvé dans la réponse')
      }
    } catch (error) {
      console.error('Erreur complète:', error)
      if (error.response?.status === 409) {
        // Le test est déjà commencé, on continue avec l'ID existant
        this.assignedTestId = testId
        await this.loadTestData()
      } else {
        this.error = error.response?.data?.message || 'Erreur lors du démarrage du test'
        this.loading = false
      }
    }
  },

  methods: {
    async loadTestData() {
      try {
        // Charger les données du test
        const response = await this.$http.get(`/tests/${this.assignedTestId}`)
        const data = response.data

        if (!data.test) {
          throw new Error('Données du test non trouvées')
        }

        this.testData = data.test
        this.timeLimit = data.timeLimit || 0
        this.progress = data.progress || { currentQuestion: 0, answers: {} }
        
        // Déterminer le type de test
        const testName = this.testData.name.toLowerCase()
        if (testName.includes('personnalité') || testName.includes('personality')) {
          this.testType = 'personality'
        } else if (testName.includes('managérial') || testName.includes('managerial')) {
          this.testType = 'managerial'
        } else if (testName.includes('commercial')) {
          this.testType = 'commercial'
        } else if (testName.includes('comportemental') || testName.includes('behavioral')) {
          this.testType = 'behavioral'
        } else if (testName.includes('motivation')) {
          this.testType = 'motivation'
        } else {
          throw new Error('Type de test non reconnu')
        }

      } catch (error) {
        console.error('Erreur lors du chargement du test:', error)
        this.error = error.response?.data?.message || error.message || 'Erreur lors du chargement du test'
      } finally {
        this.loading = false
      }
    },

    async onTestCompleted(jobApplicationId) {
      try {
        // Le test a déjà été soumis avec succès par le composant enfant.
        // Ici on se contente d'afficher un message puis de rediriger.
        await this.ToastSuccess.fire({
          title: "Test complété avec succès",
          timer: 2000
        })

        if (jobApplicationId) {
          this.$router.push(`/applications/${jobApplicationId}/tests`)
        } else {
          this.$router.push('/status')
        }
      } catch (error) {
        console.error('Erreur après la complétion du test:', error)
        await this.ToastError.fire({
          title: "Une erreur est survenue après la complétion du test",
          timer: 2000
        })
        this.$router.push('/status')
      }
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 800px;
  margin: 0 auto;
}

.alert {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>